import type { ChatMessage, FriendRequest } from '@/types/api';
import { chatApi } from '@/lib/api/chat';
import {
  connectSocket,
  disconnectSocket,
  joinUserRoom,
  onMessageSocketEvent,
  onUserSocketEvent,
} from '@/lib/chat/socketClient';
import type { ChatStoreState } from '@/store/chatStore';

type StoreGetter = () => ChatStoreState;

/** VL-MessageSent → Socket.io event `message.sent.{recipientId}` */
export type MessageSentPayload = {
  recipientId: string;
  message: ChatMessage;
};

let globalCleanupFns: Array<() => void> = [];
let messageCleanupFns: Array<() => void> = [];
let activeConversationId: string | null = null;
let registeredMessageIds = new Set<string>();
let connectedUserId: string | null = null;
let typingStopTimer: ReturnType<typeof setTimeout> | null = null;
let typingDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function findConversationIdByMessageId(get: StoreGetter, messageId: string): string | null {
  const store = get();
  for (const [conversationId, messages] of Object.entries(store.messagesByConversation)) {
    if (messages.some((m) => m.id === messageId)) return conversationId;
  }
  return null;
}

function clearMessageListeners(): void {
  for (const fn of messageCleanupFns) fn();
  messageCleanupFns = [];
  registeredMessageIds.clear();
}

function registerMessageListeners(
  conversationId: string,
  messageIds: string[],
  get: StoreGetter
): void {
  for (const messageId of messageIds) {
    if (registeredMessageIds.has(messageId)) continue;
    registeredMessageIds.add(messageId);

    messageCleanupFns.push(
      onMessageSocketEvent<{ message: ChatMessage }>('message.updated', messageId, (payload) => {
        if (!payload?.message) return;
        get().applyIncomingMessage(conversationId, payload.message);
      })
    );

    messageCleanupFns.push(
      onMessageSocketEvent<{ messageId: string }>('message.deleted', messageId, (payload) => {
        const id = payload?.messageId ?? messageId;
        get().applyMessagePatch(conversationId, id, { isDeleted: true, body: null });
      })
    );

    messageCleanupFns.push(
      onMessageSocketEvent<{ messageId: string; userId: string; emoji: string }>(
        'reaction.added',
        messageId,
        (payload) => {
          if (!payload?.userId || !payload?.emoji) return;
          get().applyReactionAdded(
            conversationId,
            payload.messageId ?? messageId,
            payload.userId,
            payload.emoji
          );
        }
      )
    );
  }
}

/**
 * Per Listaa API: message.updated / message.deleted / reaction.added use `{event}.{messageId}`.
 * Call when messages are loaded or new messages arrive in the active thread.
 */
export function syncActiveConversationMessageListeners(get: StoreGetter): void {
  const store = get();
  const conversationId = store.activeConversationId;
  if (!conversationId || conversationId !== activeConversationId) return;

  const messages = store.messagesByConversation[conversationId] ?? [];
  registerMessageListeners(
    conversationId,
    messages.map((m) => m.id),
    get
  );
}

function setupGlobalUserListeners(userId: string, get: StoreGetter): void {
  // friend.request.received.{myUserId}
  globalCleanupFns.push(
    onUserSocketEvent<{ targetUserId: string; request: FriendRequest }>(
      'friend.request.received',
      userId,
      (payload) => {
        void get().refreshRequests();
        const requester = payload?.request?.requester;
        if (payload?.request?.id != null && requester) {
          get().notifyFriendRequestAlert({
            type: 'received',
            requestId: payload.request.id,
            userId: requester.id,
            userName: requester.name || requester.username || 'Someone',
            userAvatar: requester.avatar ?? null,
          });
        }
      }
    )
  );

  // friend.request.accepted.{myUserId}
  globalCleanupFns.push(
    onUserSocketEvent<{ targetUserId: string; request: FriendRequest }>(
      'friend.request.accepted',
      userId,
      (payload) => {
        void Promise.all([get().refreshRequests(), get().refreshFriends()]);
        const addressee = payload?.request?.addressee;
        if (payload?.request?.id != null && addressee) {
          get().notifyFriendRequestAlert({
            type: 'accepted',
            requestId: payload.request.id,
            userId: addressee.id,
            userName: addressee.name || addressee.username || 'Someone',
            userAvatar: addressee.avatar ?? null,
          });
        }
      }
    )
  );

  // friend.request.declined.{myUserId} — fires for the requester when the other side declines
  globalCleanupFns.push(
    onUserSocketEvent<{ targetUserId: string; request: FriendRequest }>(
      'friend.request.declined',
      userId,
      () => {
        void get().refreshRequests();
      }
    )
  );

  // friend.request.cancelled.{myUserId} — fires for the addressee when the requester cancels
  globalCleanupFns.push(
    onUserSocketEvent<{ targetUserId: string; request: FriendRequest }>(
      'friend.request.cancelled',
      userId,
      () => {
        void get().refreshRequests();
      }
    )
  );

  // friend.unfriended.{myUserId} — fires for the removed side when the other unfriends them
  globalCleanupFns.push(
    onUserSocketEvent<{ targetUserId: string; byUserId: string }>(
      'friend.unfriended',
      userId,
      () => {
        void get().refreshFriends();
      }
    )
  );

  // VL-MessageSent → listen: message.sent.{myUserId}
  // Payload: { recipientId, message }
  globalCleanupFns.push(
    onUserSocketEvent<MessageSentPayload>('message.sent', userId, async (payload) => {
      if (!payload?.message) return;
      if (payload.recipientId !== userId) return;

      const message = payload.message;
      const conversationId = message.conversationId;
      if (!conversationId) return;

      const store = get();
      if (message.senderId === store.myUserId) return;

      store.applyIncomingMessage(conversationId, message);
      registerMessageListeners(conversationId, [message.id], get);

      try {
        await chatApi.markDelivered(conversationId);
      } catch {
        /* non-blocking */
      }

      if (store.activeConversationId === conversationId) {
        void get().markConversationRead(conversationId, { silent: true });
      } else {
        void store.refreshInbox();
      }

      const preview = message.isDeleted
        ? 'Message deleted'
        : (message.body?.trim() || 'New message');
      get().notifyIncomingMessage({
        conversationId,
        senderName: message.sender?.name ?? message.sender?.username ?? 'Someone',
        senderAvatar: message.sender?.avatar ?? null,
        preview,
      });
    })
  );
  // message.delivered.{myUserId} — sender receives delivery receipts
  globalCleanupFns.push(
    onUserSocketEvent<{ messageId: string; userId: string; deliveredAt?: string }>(
      'message.delivered',
      userId,
      (payload) => {
        if (!payload?.messageId) return;
        const conversationId = findConversationIdByMessageId(get, payload.messageId);
        get().applyMessageStatuses(conversationId, [payload.messageId], 'delivered');
      }
    )
  );

  // message.read.{myUserId} — sender receives read receipts
  globalCleanupFns.push(
    onUserSocketEvent<{ messageIds: string[]; userId: string; readAt?: string }>(
      'message.read',
      userId,
      (payload) => {
        if (!payload?.messageIds?.length) return;
        const conversationId = findConversationIdByMessageId(get, payload.messageIds[0]);
        get().applyMessageStatuses(conversationId, payload.messageIds, 'seen');
      }
    )
  );

  // reaction.removed.{myUserId}
  globalCleanupFns.push(
    onUserSocketEvent<{ messageId: string; userId: string }>('reaction.removed', userId, (payload) => {
      if (!payload?.messageId) return;
      const conversationId = findConversationIdByMessageId(get, payload.messageId);
      if (!conversationId) return;
      get().applyReactionRemoved(conversationId, payload.messageId, payload.userId);
    })
  );

  // user.typing.{myUserId}
  globalCleanupFns.push(
    onUserSocketEvent<{ userId: string; isTyping: boolean }>('user.typing', userId, (payload) => {
      if (!payload?.userId) return;
      const store = get();
      const conversationId = store.activeConversationId;
      if (!conversationId) return;
      if (payload.userId === store.myUserId) return;
      store.setConversationTyping(conversationId, payload.isTyping ? payload.userId : null);
    })
  );
}

export function connectChatRealtime(userId: string, get: StoreGetter): void {
  if (connectedUserId === userId && globalCleanupFns.length > 0) {
    joinUserRoom(userId);
    connectSocket(userId);
    return;
  }

  disconnectChatRealtime();
  connectedUserId = userId;
  // Set room id before opening socket so connect handler always has joinedUserId.
  joinUserRoom(userId);
  connectSocket(userId);
  setupGlobalUserListeners(userId, get);
  void chatApi.updatePresence(true).catch(() => undefined);
}

export function subscribeToConversation(conversationId: string, get: StoreGetter): void {
  if (activeConversationId === conversationId) {
    syncActiveConversationMessageListeners(get);
    return;
  }
  clearMessageListeners();
  activeConversationId = conversationId;
  syncActiveConversationMessageListeners(get);
}

export function unsubscribeFromConversation(): void {
  clearMessageListeners();
  activeConversationId = null;
}

export function disconnectChatRealtime(): void {
  const wasConnected = connectedUserId !== null;
  for (const fn of globalCleanupFns) fn();
  globalCleanupFns = [];
  clearMessageListeners();
  activeConversationId = null;
  connectedUserId = null;
  disconnectSocket();
  if (wasConnected) {
    void chatApi.updatePresence(false).catch(() => undefined);
  }
}

/**
 * Closing the tab/browser or navigating away doesn't unmount React in time
 * for the normal disconnectChatRealtime() cleanup to run, so the member
 * would stay stuck "online". A regular fetch can be cancelled mid-flight
 * during unload, so this uses `keepalive` (backed by the browser's beacon
 * transport) to let the request outlive the page.
 */
function markOfflineOnPageClose(): void {
  if (connectedUserId === null) return;
  void chatApi.updatePresence(false, { keepalive: true }).catch(() => undefined);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', markOfflineOnPageClose);
  window.addEventListener('beforeunload', markOfflineOnPageClose);

  // If the page was only frozen into the back/forward cache (not actually
  // closed), pagehide already reported it offline above — flip it back
  // online now that the tab is visible again.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && connectedUserId !== null) {
      void chatApi.updatePresence(true).catch(() => undefined);
    }
  });
}

export function notifyTyping(conversationId: string, isTyping: boolean): void {
  if (typingDebounceTimer) clearTimeout(typingDebounceTimer);

  if (isTyping) {
    typingDebounceTimer = setTimeout(() => {
      void chatApi.setTyping(conversationId, true);
      if (typingStopTimer) clearTimeout(typingStopTimer);
      typingStopTimer = setTimeout(() => {
        void chatApi.setTyping(conversationId, false);
      }, 3000);
    }, 300);
    return;
  }

  if (typingStopTimer) clearTimeout(typingStopTimer);
  void chatApi.setTyping(conversationId, false);
}
